import { Suspense, lazy, useEffect, useState } from 'react';
import { WarehouseDocumentMutationResponseDto } from 'woofwoof-api';
import {
  CreateSaleVariables,
  useCreateWarehouseSale,
} from '../../hooks/useCreateWarehouseSale';
import {
  pinnedSaleKey,
  useSaleOperationKey,
} from '../../hooks/useSaleOperationKey';
import { WarehouseErrorCode, warehouseErrorCode } from '../../utils/errors';

const loadSaleForm = (): Promise<typeof import('./WarehouseSaleFormModal')> =>
  import('./WarehouseSaleFormModal');
const WarehouseSaleFormModal = lazy(loadSaleForm);

/** A refused attempt and the key it sent, so a late answer never labels another series. */
interface SaleFailure {
  key: string;
  code: WarehouseErrorCode | undefined;
}

interface WarehouseSaleEntryProps {
  open: boolean;
  organizationId: string;
  /** The visit menu's visit; absent from Produkty. */
  visitId?: string;
  onClose: () => void;
}

/**
 * The Sale side of an entry point ("Nowa sprzedaż", the visit's "Sprzedaż").
 * The host renders it for as long as it offers the Sale, not only while the
 * form is open, so the entry point holds the Sale's series and its mutation
 * (R2-4, F-1). The form closes once the mutation reports the Sale created.
 */
export default function WarehouseSaleEntry({
  open,
  organizationId,
  visitId,
  onClose,
}: WarehouseSaleEntryProps): JSX.Element {
  const operationKeys = useSaleOperationKey(organizationId, visitId);
  const { mutateAsync, isLoading } = useCreateWarehouseSale({
    onCreated: onClose,
  });
  // F-8: the series' last refusal lives here, not in the form, which unmounts
  // on close; a reopened form then still says the Warehouse is off.
  const [failure, setFailure] = useState<SaleFailure | null>(null);
  const blocked =
    operationKeys.pending &&
    failure?.code === 'WAREHOUSE_DISABLED' &&
    failure.key === pinnedSaleKey(operationKeys);

  // Only the series' own answers change its label: a late answer of an ended
  // series is ignored, as the registry ignores its settle.
  const submit = async (
    variables: CreateSaleVariables,
  ): Promise<WarehouseDocumentMutationResponseDto> => {
    const key = variables.dto.operationKey;
    try {
      const sale = await mutateAsync(variables);
      setFailure((last) => (last?.key === key ? null : last));
      return sale;
    } catch (error) {
      if (key === pinnedSaleKey(operationKeys)) {
        setFailure({ key, code: warehouseErrorCode(error) });
      }
      throw error;
    }
  };

  // Pre-warm the form's chunk, so the first click opens it at once.
  useEffect(() => {
    loadSaleForm().catch(() => undefined);
  }, []);

  return (
    <Suspense fallback={null}>
      {open && (
        <WarehouseSaleFormModal
          open
          organizationId={organizationId}
          visitId={visitId}
          isSubmitting={isLoading}
          operationKeys={operationKeys}
          blocked={blocked}
          onClose={onClose}
          onSubmit={submit}
        />
      )}
    </Suspense>
  );
}
